import type { FleetTodoItem } from "../controllers/fleet-todos.ts";
import type { RunTimelineEntry } from "../operator-trust/state.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { extractText } from "./message-extract.ts";
import { selectSessionThinking, type ThinkingLine } from "./thinking-selector.ts";

export type ActivityThinkingLine = ThinkingLine;

export type ActivityExploreItem = {
  id: string;
  at: number;
  source: string;
  label: string;
  detail?: string;
  active?: boolean;
};

/** Maximum events expanded per subagent card into the activity stream.
 *  Cards can carry up to 500 events each (`addEvent` ring buffer) and one
 *  session can fan out to many cards. Without this cap, the stream can
 *  reach tens of thousands of entries on long-running orchestrations,
 *  and the chip count + filter/sort cost freeze the webchat on every
 *  poll. We only need the *most recent* events for the panel UI; the
 *  full ring buffer stays in `card.events` for the agent inspector. */
export const MAX_EVENTS_PER_CARD_FOR_ACTIVITY = 200;

export type ActivityTodoItem = {
  id: string;
  at: number;
  text: string;
  done: boolean;
  source: string;
};

/** Unified Activity stream kinds (filter chips). */
export type ActivityStreamKind = "thinking" | "exec" | "todo" | "health";

export type ActivityStreamEntry = {
  id: string;
  kind: ActivityStreamKind;
  at: number;
  source: string;
  /** One-line scan summary. */
  summary: string;
  /** Full detail shown when the card is expanded. */
  body?: string;
  active?: boolean;
  done?: boolean;
};

const TODO_LINE_RE = /^\s*[-*]\s+\[([ xX])\]\s+(.+)$/;

function parseTodosFromText(
  text: string,
  source: string,
  seen: Set<string>,
  at: number,
): ActivityTodoItem[] {
  const out: ActivityTodoItem[] = [];
  for (const line of text.split("\n")) {
    const match = TODO_LINE_RE.exec(line);
    if (!match) {
      continue;
    }
    const body = match[2]?.trim() ?? "";
    if (!body) {
      continue;
    }
    const key = `${source}:${body}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push({
      id: key,
      at,
      text: body,
      done: match[1]?.toLowerCase() === "x",
      source,
    });
  }
  return out;
}

/** First non-empty line, trimmed and length-capped for the card summary. */
export function summarizeActivityText(text: string, max = 140): string {
  const line = text
    .split("\n")
    .map((part) => part.trim())
    .find((part) => part.length > 0);
  if (!line) {
    return "";
  }
  if (line.length <= max) {
    return line;
  }
  return `${line.slice(0, max - 1)}…`;
}

/**
 * JAISIU-3752 — derive the "I am now working" one-line summary shown
 * in the live assistant bubble and the composer footer pill, so both
 * surfaces agree with the Inspector's thinking card.
 *
 * Priority (first non-empty wins):
 *   1. `reasoningStream` — the streaming reasoning delta from the model.
 *   2. `stream` — the main response stream (the model starts to type the
 *      final answer before reasoning fully resolves).
 *   3. `lastAssistantThinking` — fallback to the most recent assistant
 *      message's `extractThinking`, so the summary survives a chat-tab
 *      switch or a hard refresh that re-loads the chat history.
 *
 * Returns an empty string when no thinking line is available. Callers
 * should treat `""` as "no summary" and fall through to their existing
 * phase label (e.g. `Running tools…`, `Editing file…`).
 */
export function resolveLiveThinkingSummary(params: {
  reasoningStream?: string | null;
  stream?: string | null;
  lastAssistantThinking?: string | null;
}): string {
  const reasoning = params.reasoningStream?.trim();
  if (reasoning) {
    return summarizeActivityText(reasoning);
  }
  const main = params.stream?.trim();
  if (main) {
    return summarizeActivityText(main);
  }
  const last = params.lastAssistantThinking?.trim();
  if (last) {
    return summarizeActivityText(last);
  }
  return "";
}

/** Resolve a human label for a tool stream / history message. */
export function resolveToolActivityLabel(message: unknown): string {
  if (!message || typeof message !== "object") {
    return "tool";
  }
  const m = message as Record<string, unknown>;
  for (const key of ["toolName", "tool_name", "name"] as const) {
    const value = m[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  if (Array.isArray(m.content)) {
    for (const item of m.content) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const x = item as Record<string, unknown>;
      const t = (typeof x.type === "string" ? x.type : "").toLowerCase();
      if (
        t === "toolcall" ||
        t === "tool_use" ||
        t === "toolresult" ||
        t === "tool_result" ||
        t === "function"
      ) {
        if (typeof x.name === "string" && x.name.trim()) {
          return x.name.trim();
        }
      }
    }
  }
  return "tool";
}

function isToolActivityMessage(message: unknown): boolean {
  if (!message || typeof message !== "object") {
    return false;
  }
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role === "toolresult" || role === "tool_result" || role === "tool" || role === "function") {
    return true;
  }
  if (typeof m.toolCallId === "string" || typeof m.tool_call_id === "string") {
    return true;
  }
  if (typeof m.toolName === "string" || typeof m.tool_name === "string") {
    return true;
  }
  if (!Array.isArray(m.content)) {
    return false;
  }
  return m.content.some((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }
    const t = (
      typeof (item as { type?: unknown }).type === "string" ? (item as { type: string }).type : ""
    ).toLowerCase();
    return t === "toolcall" || t === "tool_use" || t === "toolresult" || t === "tool_result";
  });
}

function extractToolDetail(message: unknown): string | undefined {
  const text = extractText(message)?.trim();
  if (text) {
    return text.slice(0, 2000);
  }
  if (!message || typeof message !== "object") {
    return undefined;
  }
  const content = (message as { content?: unknown }).content;
  if (!Array.isArray(content)) {
    return undefined;
  }
  for (const item of content) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const x = item as Record<string, unknown>;
    const t = (typeof x.type === "string" ? x.type : "").toLowerCase();
    if (
      (t === "toolresult" || t === "tool_result") &&
      typeof x.text === "string" &&
      x.text.trim()
    ) {
      return x.text.trim().slice(0, 2000);
    }
  }
  return undefined;
}

function mergeFleetTodos(
  fleetTodos: FleetTodoItem[] | undefined,
  seen: Set<string>,
): ActivityTodoItem[] {
  if (!fleetTodos?.length) {
    return [];
  }
  const out: ActivityTodoItem[] = [];
  for (let i = 0; i < fleetTodos.length; i += 1) {
    const item = fleetTodos[i]!;
    const text = item.content.trim();
    if (!text) {
      continue;
    }
    const key = `fleet:${item.id}`;
    if (seen.has(key) || seen.has(`Session:${text}`)) {
      continue;
    }
    seen.add(key);
    seen.add(`Session:${text}`);
    const inProgress = item.status === "in_progress";
    out.push({
      id: key,
      // Stable slot above message indices; in-progress ranks slightly higher.
      at: 1_000_000 + (inProgress ? 500_000 : 0) + i,
      text,
      done: item.status === "completed" || item.status === "cancelled",
      source: inProgress
        ? "Fleet · in progress"
        : item.status === "cancelled"
          ? "Fleet · cancelled"
          : item.status === "completed"
            ? "Fleet · done"
            : "Fleet",
    });
  }
  return out;
}

export function collectActivityState(params: {
  messages: unknown[];
  chatToolMessages: unknown[];
  stream: string | null;
  reasoningStream?: string | null;
  subagents: ChatSubagentCard[];
  /** When false, historical thinking lines are omitted (live stream still collected). */
  showThinking: boolean;
  runActive?: boolean;
  liveRunHint?: string | null;
  activeFileToolPath?: string | null;
  fleetTodos?: FleetTodoItem[];
}): {
  thinking: ActivityThinkingLine[];
  explore: ActivityExploreItem[];
  todos: ActivityTodoItem[];
} {
  const thinking: ActivityThinkingLine[] = [];
  const explore: ActivityExploreItem[] = [];
  const exploreSeen = new Set<string>();
  const todoSeen = new Set<string>();
  const todos: ActivityTodoItem[] = [];

  // Single source of truth for thinking content — delegated to
  // `selectSessionThinking` so the activity panel and the transcript
  // bubble (and any future Inspector sub-tab) read identical data.
  const thinkingScope = selectSessionThinking({
    messages: params.messages,
    chatToolMessages: params.chatToolMessages,
    reasoningStream: params.reasoningStream,
    stream: params.stream,
    subagents: params.subagents,
    showThinking: params.showThinking,
    maxEventsPerSubagent: MAX_EVENTS_PER_CARD_FOR_ACTIVITY,
  });
  for (const line of thinkingScope.lines) {
    thinking.push(line);
  }

  const pushExplore = (item: ActivityExploreItem) => {
    const dedupe = `${item.source}:${item.label}:${item.detail ?? ""}`;
    if (exploreSeen.has(dedupe)) {
      return;
    }
    exploreSeen.add(dedupe);
    explore.push(item);
  };

  // Always collect session tools + markdown todos for the activity panel.
  // (showThinking only gates historical reasoning — tools/todos are operational state.)
  // Thinking collection is delegated to `selectThinkingContent` above so
  // the activity panel and the transcript bubble share one source.
  params.messages.forEach((message, index) => {
    const role = (message as { role?: string }).role;
    if (role === "assistant") {
      const text = extractText(message);
      if (text) {
        todos.push(...parseTodosFromText(text, "Session", todoSeen, index));
      }
    }
    if (isToolActivityMessage(message)) {
      const label = resolveToolActivityLabel(message);
      pushExplore({
        id: `hist-tool:${index}`,
        at: index,
        source: "Session",
        label,
        detail: extractToolDetail(message),
      });
    }
  });

  for (let i = 0; i < params.chatToolMessages.length; i += 1) {
    const toolMsg = params.chatToolMessages[i];
    pushExplore({
      id: `tool-msg:${i}`,
      at: i,
      source: "Session",
      label: resolveToolActivityLabel(toolMsg),
      detail: extractToolDetail(toolMsg),
      active: params.runActive === true,
    });
  }

  todos.push(...mergeFleetTodos(params.fleetTodos, todoSeen));

  if (params.runActive) {
    const hint = params.liveRunHint?.trim();
    const filePath = params.activeFileToolPath?.trim();
    if (hint) {
      pushExplore({
        id: "live-hint",
        at: Date.now(),
        source: "Session",
        label: hint,
        detail: filePath || undefined,
        active: true,
      });
    } else if (filePath) {
      pushExplore({
        id: "live-file",
        at: Date.now(),
        source: "Session",
        label: "Editing file…",
        detail: filePath,
        active: true,
      });
    }
  }

  for (const card of params.subagents) {
    const source = card.label || card.agentId;
    // Subagent thinking (streamText + thinking-phase events) is
    // collected by `selectThinkingContent` above so the activity
    // panel and the transcript bubble share the same source.
    if (card.task?.trim()) {
      todos.push(
        ...parseTodosFromText(
          `- [ ] ${card.task}`,
          source,
          todoSeen,
          card.updatedAt ?? card.createdAt,
        ),
      );
    }
    for (const event of (card.events ?? []).slice(0, MAX_EVENTS_PER_CARD_FOR_ACTIVITY)) {
      // Cap the per-card event expansion so a single subagent with a
      // 500-event ring buffer cannot dominate the activity stream.
      // The inspector's full history lives in `card.events` and is
      // unaffected by this slice. The activity panel is reverse-chrono,
      // JAISIU-types-sweep: `label` / `detail` are optional on the
      // chat-event schema (older gateway versions emit events with
      // empty labels for `tool` / `lifecycle` phases). Coalesce to
      // empty strings so downstream `toLowerCase().includes(...)`
      // and the `label` rendering don't have to thread a separate
      // undefined check.
      const label = event.label ?? "";
      const detail = event.detail ?? "";
      if (event.stream === "tool") {
        pushExplore({
          id: `sub:${card.id}:${event.seq}`,
          at: event.ts,
          source,
          label,
          detail,
          active: card.status === "running" || card.status === "spawning",
        });
      }
    }
    if (card.toolHint) {
      pushExplore({
        id: `sub-hint:${card.id}`,
        at: card.updatedAt ?? card.createdAt,
        source,
        label: card.toolHint,
        active: card.status === "running",
      });
    }
  }

  thinking.sort((a, b) => a.at - b.at);
  explore.sort((a, b) => a.at - b.at);
  todos.sort((a, b) => a.at - b.at);

  return { thinking, explore, todos };
}

/**
 * Merge Thinking / Exec / Todo / Health into one reverse-chronological stream
 * (newest first). Optional `connected` / subagent errors contribute Health rows.
 */
export function buildActivityStream(
  state: {
    thinking: ActivityThinkingLine[];
    explore: ActivityExploreItem[];
    todos: ActivityTodoItem[];
  },
  options?: {
    connected?: boolean;
    subagents?: ChatSubagentCard[];
    runTimeline?: RunTimelineEntry[];
    sessionHealth?: "healthy" | "suspect" | "wedged" | null;
    sessionHealthReason?: string | null;
    turnPhase?: string | null;
  },
): ActivityStreamEntry[] {
  const entries: ActivityStreamEntry[] = [];

  for (const line of state.thinking) {
    const summary = summarizeActivityText(line.text);
    if (!summary) {
      continue;
    }
    entries.push({
      id: line.id,
      kind: "thinking",
      at: line.at,
      source: line.source,
      summary,
      body: line.text,
      active: line.source.includes("live"),
    });
  }

  for (const item of state.explore) {
    const summary = item.label.trim() || "tool";
    const bodyParts = [item.detail?.trim(), item.source !== "Session" ? item.source : ""]
      .filter(Boolean)
      .join("\n");
    entries.push({
      id: item.id,
      kind: "exec",
      at: item.at,
      source: item.source,
      summary,
      body: bodyParts || undefined,
      active: item.active,
    });
  }

  for (const todo of state.todos) {
    const mark = todo.done ? "[x]" : "[ ]";
    entries.push({
      id: todo.id,
      kind: "todo",
      at: todo.at,
      source: todo.source,
      summary: `Todo ${todo.done ? "done" : "created"} · ${mark} ${todo.text}`,
      body: `${mark} ${todo.text}\n${todo.source}`,
      done: todo.done,
    });
  }

  const subagents = options?.subagents ?? [];
  for (const card of subagents) {
    if (card.status !== "error") {
      continue;
    }
    const label = card.label || card.agentId || "subagent";
    entries.push({
      id: `health:error:${card.id}`,
      kind: "health",
      at: card.updatedAt ?? card.createdAt,
      source: label,
      summary: `Subagent error · ${label}`,
      body: card.task?.trim() || undefined,
    });
  }

  if (options?.connected === false) {
    entries.push({
      id: "health:gateway-disconnected",
      kind: "health",
      at: Date.now(),
      source: "System",
      summary: "Gateway disconnected",
      body: "Activity stream may be stale until the gateway reconnects.",
    });
  }

  if (options?.turnPhase) {
    entries.push({
      id: `health:turn:${options.turnPhase}`,
      kind: "health",
      at: Date.now(),
      source: "Coordinator",
      summary: `Turn ${options.turnPhase.replace(/_/g, " ")}`,
    });
  }

  if (options?.sessionHealth && options.sessionHealth !== "healthy") {
    entries.push({
      id: `health:session:${options.sessionHealth}`,
      kind: "health",
      at: Date.now(),
      source: "Session",
      summary:
        options.sessionHealth === "wedged" ? "Session wedged" : "Session slow — still working",
      body: options.sessionHealthReason ?? undefined,
    });
  }

  for (const row of options?.runTimeline ?? []) {
    entries.push({
      id: row.id,
      kind: "health",
      at: row.atMs,
      source: row.kind,
      summary: row.summary,
      body: row.detail ?? undefined,
    });
  }

  entries.sort((a, b) => {
    if (b.at !== a.at) {
      return b.at - a.at;
    }
    return a.id.localeCompare(b.id);
  });
  return entries;
}

export function filterActivityStream(
  entries: ActivityStreamEntry[],
  filter: ActivityStreamKind | "all",
  search = "",
): ActivityStreamEntry[] {
  const q = search.trim().toLowerCase();
  return entries.filter((entry) => {
    if (filter !== "all" && entry.kind !== filter) {
      return false;
    }
    if (!q) {
      return true;
    }
    const hay = `${entry.summary}\n${entry.body ?? ""}\n${entry.source}`.toLowerCase();
    return hay.includes(q);
  });
}
